import { useState } from "react";
import { Mail, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { trpc } from "../lib/trpc";

/**
 * Login page - Magic link authentication
 */
export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const requestMagicLink = trpc.auth.requestMagicLink.useMutation({
    onSuccess: () => {
      setIsSubmitted(true);
      toast.success("Magic link sent! Check your email.");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to send magic link");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    requestMagicLink.mutate({ email });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 relative overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-primary/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-brand-accent/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
      </div>

      {/* TeleCloud Employees Link */}
      <motion.a
        href="/admin/login"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
        className="absolute top-6 right-6 text-sm text-muted-foreground hover:text-brand-primary font-medium transition-colors"
      >
        TeleCloud Employees →
      </motion.a>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-md"
      >
        {/* Hero Section */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 20, delay: 0.2 }}
            className="inline-flex items-center justify-center w-20 h-20 bg-brand-primary rounded-2xl mb-6 shadow-glow-primary animate-glow-pulse"
          >
            <Sparkles className="w-10 h-10 text-white" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl font-bold text-brand-primary mb-3"
          >
            TeleCloud Launchpad
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-muted-foreground text-lg"
          >
            Premium VoIP Provisioning Platform
          </motion.p>
        </div>

        {/* Login Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card variant="glass">
            <CardHeader>
              <CardTitle>Welcome back</CardTitle>
              <CardDescription>
                {isSubmitted
                  ? "Check your email for the magic link"
                  : "Enter your email to receive a magic link"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="text-center py-8"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", delay: 0.1 }}
                    className="inline-flex items-center justify-center w-20 h-20 bg-brand-success rounded-full mb-6 shadow-glow-success"
                  >
                    <Mail className="w-10 h-10 text-white" />
                  </motion.div>

                  <h3 className="text-xl font-semibold mb-3">Check your email</h3>
                  <p className="text-sm text-muted-foreground mb-8">
                    We've sent a magic link to<br />
                    <strong className="text-foreground">{email}</strong>
                  </p>

                  <Button
                    variant="outline-brand"
                    onClick={() => {
                      setIsSubmitted(false);
                      setEmail("");
                    }}
                  >
                    Use a different email
                  </Button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" required>Email address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={requestMagicLink.isPending}
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="brand"
                    size="lg"
                    className="w-full"
                    disabled={requestMagicLink.isPending || !email}
                  >
                    {requestMagicLink.isPending ? (
                      <>Sending magic link...</>
                    ) : (
                      <>
                        <Mail className="w-5 h-5" />
                        Send magic link
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="text-center text-sm text-muted-foreground mt-8"
        >
          Don't have an account? Contact your administrator for an invitation.
        </motion.p>
      </motion.div>
    </div>
  );
}
